import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import MacrosAPI from '../../api/macros';
import { throwErrorMessage } from '../utils/api';

let recordsRequestSequence = 0;
let managementRequestSequence = 0;

export const state = {
  records: [],
  managementRecords: [],
  uiFlags: {
    isFetchingItem: false,
    isFetching: false,
    isCreating: false,
    isDeleting: false,
    isUpdating: false,
    isExecuting: false,
    isFetchingManagement: false,
    isCloning: false,
  },
};

export const getters = {
  getMacros($state) {
    return $state.records;
  },
  getMacro: $state => id => {
    return [...$state.records, ...$state.managementRecords].find(
      record => record.id === Number(id)
    );
  },
  getManagementMacros($state) {
    return $state.managementRecords;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
};

export const actions = {
  get: async function getMacros(
    { commit, state: currentState, rootGetters },
    options = {}
  ) {
    const accountId = rootGetters?.getCurrentAccountId ?? 'default';
    const cacheEnabled = Boolean(currentState && rootGetters);
    if (
      cacheEnabled &&
      options?.force !== true &&
      (currentState.uiFlags.fetchedAccountId === accountId ||
        currentState.uiFlags.fetchingAccountId === accountId)
    )
      return;

    recordsRequestSequence += 1;
    const requestId = recordsRequestSequence;
    if (cacheEnabled && currentState.uiFlags.recordsAccountId !== accountId) {
      commit(types.SET_MACROS, []);
      commit(types.SET_MACROS_UI_FLAG, {
        recordsAccountId: accountId,
        fetchedAccountId: null,
      });
    }

    commit(
      types.SET_MACROS_UI_FLAG,
      cacheEnabled
        ? { isFetching: true, fetchingAccountId: accountId, requestId }
        : { isFetching: true }
    );
    try {
      const response = await MacrosAPI.get();
      if (
        cacheEnabled &&
        (rootGetters.getCurrentAccountId !== accountId ||
          currentState.uiFlags.requestId !== requestId)
      )
        return;

      commit(types.SET_MACROS, response.data.payload);
      if (cacheEnabled) {
        commit(types.SET_MACROS_UI_FLAG, {
          fetchedAccountId: accountId,
          recordsAccountId: accountId,
        });
      }
    } catch (error) {
      // Ignore error
    } finally {
      if (!cacheEnabled || currentState.uiFlags.requestId === requestId) {
        commit(
          types.SET_MACROS_UI_FLAG,
          cacheEnabled
            ? { isFetching: false, fetchingAccountId: null, requestId: null }
            : { isFetching: false }
        );
      }
    }
  },
  getForManagement: async function getManagementMacros(
    { commit, state: currentState, rootGetters },
    options = {}
  ) {
    const accountId = rootGetters?.getCurrentAccountId ?? 'default';
    const cacheEnabled = Boolean(currentState && rootGetters);
    if (
      cacheEnabled &&
      options?.force !== true &&
      (currentState.uiFlags.managementFetchedAccountId === accountId ||
        currentState.uiFlags.managementFetchingAccountId === accountId)
    )
      return;

    managementRequestSequence += 1;
    const requestId = managementRequestSequence;
    if (
      cacheEnabled &&
      currentState.uiFlags.managementRecordsAccountId !== accountId
    ) {
      commit(types.SET_MANAGEMENT_MACROS, []);
      commit(types.SET_MACROS_UI_FLAG, {
        managementRecordsAccountId: accountId,
        managementFetchedAccountId: null,
      });
    }

    commit(
      types.SET_MACROS_UI_FLAG,
      cacheEnabled
        ? {
            isFetchingManagement: true,
            managementFetchingAccountId: accountId,
            managementRequestId: requestId,
          }
        : { isFetchingManagement: true }
    );
    try {
      const response = await MacrosAPI.getForManagement();
      if (
        cacheEnabled &&
        (rootGetters.getCurrentAccountId !== accountId ||
          currentState.uiFlags.managementRequestId !== requestId)
      )
        return;

      commit(types.SET_MANAGEMENT_MACROS, response.data.payload);
      if (cacheEnabled) {
        commit(types.SET_MACROS_UI_FLAG, {
          managementFetchedAccountId: accountId,
          managementRecordsAccountId: accountId,
        });
      }
    } catch (error) {
      // Ignore error
    } finally {
      if (
        !cacheEnabled ||
        currentState.uiFlags.managementRequestId === requestId
      ) {
        commit(
          types.SET_MACROS_UI_FLAG,
          cacheEnabled
            ? {
                isFetchingManagement: false,
                managementFetchingAccountId: null,
                managementRequestId: null,
              }
            : { isFetchingManagement: false }
        );
      }
    }
  },
  revalidate: async ({ dispatch, state: currentState, rootGetters }) => {
    const accountId = rootGetters.getCurrentAccountId;
    const requests = [];

    if (currentState.uiFlags.recordsAccountId === accountId) {
      requests.push(dispatch('get', { force: true }));
    }
    if (currentState.uiFlags.managementRecordsAccountId === accountId) {
      requests.push(dispatch('getForManagement', { force: true }));
    }

    await Promise.all(requests);
  },
  getSingleMacro: async function getMacroById({ commit }, macroId) {
    commit(types.SET_MACROS_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await MacrosAPI.show(macroId);
      return response.data.payload;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_MACROS_UI_FLAG, { isFetchingItem: false });
    }
  },
  create: async function createMacro({ commit }, macrosObj) {
    commit(types.SET_MACROS_UI_FLAG, { isCreating: true });
    try {
      const response = await MacrosAPI.create(macrosObj);
      commit(types.SET_MACROS_UI_FLAG, {
        fetchedAccountId: null,
        managementFetchedAccountId: null,
      });
      return response.data.payload;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_MACROS_UI_FLAG, { isCreating: false });
    }
  },
  execute: async function executeMacro({ commit }, macrosObj) {
    commit(types.SET_MACROS_UI_FLAG, { isExecuting: true });
    try {
      await MacrosAPI.executeMacro(macrosObj);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_MACROS_UI_FLAG, { isExecuting: false });
    }
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_MACROS_UI_FLAG, { isUpdating: true });
    try {
      const response = await MacrosAPI.update(id, updateObj);
      commit(types.SET_MACROS_UI_FLAG, {
        fetchedAccountId: null,
        managementFetchedAccountId: null,
      });
      return response.data.payload;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_MACROS_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_MACROS_UI_FLAG, { isDeleting: true });
    try {
      await MacrosAPI.delete(id);
      commit(types.DELETE_MACRO, id);
      commit(types.DELETE_MANAGEMENT_MACRO, id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_MACROS_UI_FLAG, { isDeleting: false });
    }
  },
  clone: async ({ commit }, id) => {
    commit(types.SET_MACROS_UI_FLAG, { isCloning: true });
    try {
      await MacrosAPI.clone(id);
      commit(types.SET_MACROS_UI_FLAG, {
        fetchedAccountId: null,
        managementFetchedAccountId: null,
      });
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_MACROS_UI_FLAG, { isCloning: false });
    }
  },
};

export const mutations = {
  [types.SET_MACROS_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.ADD_MACRO]: MutationHelpers.setSingleRecord,
  [types.SET_MACROS]: MutationHelpers.set,
  [types.SET_MANAGEMENT_MACROS]($state, records) {
    $state.managementRecords = records;
  },
  [types.EDIT_MACRO]: MutationHelpers.update,
  [types.DELETE_MACRO]: MutationHelpers.destroy,
  [types.DELETE_MANAGEMENT_MACRO]($state, id) {
    $state.managementRecords = $state.managementRecords.filter(
      record => record.id !== id
    );
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
